/**
 * Marketing Attribution Parameters Utility
 *
 * Captures and persists marketing attribution parameters from WordPress handoff.
 * These parameters enable partner attribution to flow seamlessly across the
 * WordPress marketing site → React app boundary.
 *
 * Usage:
 * - Call captureMarketingParams() on app initialization (in index.tsx or App.tsx)
 * - Use getStoredParams() to retrieve stored parameters
 * - Use getReturnUrl() to get the intended destination after auth
 * - Use getSelectedPlan() to get the pricing plan from WordPress
 */

export interface MarketingParams {
  /** Partner identifier (e.g., 'socs', 'spgs') */
  partnerId?: string;
  /** School identifier for partner attribution */
  schoolId?: string;
  /** URL to redirect to after authentication */
  returnTo?: string;
  /** Selected pricing plan from WordPress (e.g., 'PREMIUM') */
  plan?: string;
  /** UTM source for analytics */
  utmSource?: string;
  /** UTM medium for analytics */
  utmMedium?: string;
  /** UTM campaign for analytics */
  utmCampaign?: string;
  /** UTM term for analytics */
  utmTerm?: string;
  /** UTM content for analytics */
  utmContent?: string;
  /** Referral code */
  ref?: string;
}

// Storage key prefix for sessionStorage
const STORAGE_PREFIX = 'pedagogue_marketing_';

// Map URL param names to storage keys
const PARAM_MAP: Record<string, keyof MarketingParams> = {
  partner_id: 'partnerId',
  school_id: 'schoolId',
  return_to: 'returnTo',
  plan: 'plan',
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_term: 'utmTerm',
  utm_content: 'utmContent',
  ref: 'ref',
};

/**
 * Captures marketing attribution parameters from the current URL.
 * Should be called on app initialization to capture params from WordPress handoff.
 *
 * @returns The captured marketing parameters
 */
export const captureMarketingParams = (): MarketingParams => {
  const urlParams = new URLSearchParams(window.location.search);
  const params: MarketingParams = {};

  // Extract params from URL
  Object.entries(PARAM_MAP).forEach(([urlParam, storageKey]) => {
    const value = urlParams.get(urlParam);
    if (value) {
      params[storageKey] = value;
    }
  });

  // Persist non-empty params to sessionStorage
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, value);
    }
  });

  return params;
};

/**
 * Retrieves all stored marketing parameters from sessionStorage.
 *
 * @returns The stored marketing parameters
 */
export const getStoredParams = (): MarketingParams => {
  const params: MarketingParams = {};

  Object.values(PARAM_MAP).forEach((storageKey) => {
    const value = sessionStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (value) {
      params[storageKey] = value;
    }
  });

  return params;
};

/**
 * Allowlist of internal paths accepted for return_to redirects.
 * Prevents open redirect vulnerabilities.
 */
const ALLOWED_RETURN_PREFIXES = [
  '/courses',
  '/master-courses/',
  '/passport',
  '/certificates',
  '/lessons',
  '/onboarding/',
  '/settings',
];

/**
 * Gets the return URL to redirect to after authentication.
 * Validates against an allowlist of internal paths to prevent open redirects.
 * Defaults to '/courses' if no return_to was specified or if validation fails.
 *
 * @returns The validated return URL path
 */
export const getReturnUrl = (): string => {
  const stored = sessionStorage.getItem(`${STORAGE_PREFIX}returnTo`);
  if (!stored) return '/courses';
  // Must be a relative path (no protocol/host)
  if (!stored.startsWith('/') || stored.startsWith('//')) return '/courses';
  // Validate against allowlist
  const isAllowed = ALLOWED_RETURN_PREFIXES.some(
    (prefix) => stored === prefix || stored.startsWith(prefix)
  );
  return isAllowed ? stored : '/courses';
};

/**
 * Gets the selected pricing plan from WordPress.
 *
 * @returns The plan name (e.g., 'PREMIUM') or null if not specified
 */
export const getSelectedPlan = (): string | null => {
  return sessionStorage.getItem(`${STORAGE_PREFIX}plan`);
};

/**
 * Gets the partner ID for attribution.
 *
 * @returns The partner ID or null if not specified
 */
export const getPartnerId = (): string | null => {
  return sessionStorage.getItem(`${STORAGE_PREFIX}partnerId`);
};

/**
 * Gets the school ID for attribution.
 *
 * @returns The school ID or null if not specified
 */
export const getSchoolId = (): string | null => {
  return sessionStorage.getItem(`${STORAGE_PREFIX}schoolId`);
};

/**
 * Clears all stored marketing parameters.
 * Call this after the attribution has been recorded (e.g., after successful auth).
 */
export const clearMarketingParams = (): void => {
  Object.values(PARAM_MAP).forEach((storageKey) => {
    sessionStorage.removeItem(`${STORAGE_PREFIX}${storageKey}`);
  });
};

/**
 * Builds a URL with marketing parameters appended.
 * Useful for maintaining attribution when linking to other pages.
 *
 * @param baseUrl - The base URL to append params to
 * @param includeUtm - Whether to include UTM parameters (default: true)
 * @returns URL with marketing parameters appended
 */
export const buildUrlWithParams = (
  baseUrl: string,
  includeUtm: boolean = true
): string => {
  const params = getStoredParams();
  const url = new URL(baseUrl, window.location.origin);

  // Always include attribution params
  if (params.partnerId) url.searchParams.set('partner_id', params.partnerId);
  if (params.schoolId) url.searchParams.set('school_id', params.schoolId);
  if (params.ref) url.searchParams.set('ref', params.ref);

  // Optionally include UTM params
  if (includeUtm) {
    if (params.utmSource) url.searchParams.set('utm_source', params.utmSource);
    if (params.utmMedium) url.searchParams.set('utm_medium', params.utmMedium);
    if (params.utmCampaign)
      url.searchParams.set('utm_campaign', params.utmCampaign);
    if (params.utmTerm) url.searchParams.set('utm_term', params.utmTerm);
    if (params.utmContent)
      url.searchParams.set('utm_content', params.utmContent);
  }

  return url.toString();
};

/**
 * Gets all UTM parameters as an object.
 * Useful for analytics tracking.
 *
 * @returns Object containing UTM parameters
 */
export const getUtmParams = (): Pick<
  MarketingParams,
  'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmTerm' | 'utmContent'
> => {
  const params = getStoredParams();
  return {
    utmSource: params.utmSource,
    utmMedium: params.utmMedium,
    utmCampaign: params.utmCampaign,
    utmTerm: params.utmTerm,
    utmContent: params.utmContent,
  };
};
